<template>
	<div class="datatable" v-if="isAuthenticated && !systemLoading">
		<b-message type="is-warning" v-if="rows.length === 0 && searchQuery !== ''">
			<strong>Oops!</strong> We couldn't find anything with that search, try something else?
		</b-message>

		<div class="datatable-table" ref="datatableStart">
			<b-loading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></b-loading>
			<div class="datatable-header">
				<div class="columns is-desktop">
					<div class="column is-5" v-if="featureAvailable('actions')">
						<div class="datatable-selector datatable-selector-toggle">
							<div class="datatable-selector-dropdown">
								<b-checkbox v-model="selectedAll" v-on:input="selectDefaultAction" title="Select All" />
								<b-dropdown ref="rowSelector">
									<button slot="trigger">
										<b-icon icon="angle-down" size="is-small"></b-icon>
									</button>

									<b-dropdown-item v-on:click="selectEveryRecord">
										All
									</b-dropdown-item>

									<b-dropdown-item v-on:click="selectAll">
										Just this page
									</b-dropdown-item>

									<b-dropdown-item v-on:click="selectNone">
										None
									</b-dropdown-item>
								</b-dropdown>
							</div>
							<div class="datatable-selector-actions" v-if="selectedIds.length">
								<b-dropdown>
									<button class="button is-text" slot="trigger"
									        v-on:click="getActions">
										<span>Actions</span>
										<b-icon icon="angle-down" size="is-small"></b-icon>
									</button>

									<div class="actions" :class="{'loading': loadingActions}">
										<b-loading
											:is-full-page="false"
											:active.sync="loadingActions"
											:can-cancel="false"
										/>

										<b-dropdown-item
											v-for="(data, key) in actions"
											:key="`action-${key}`"
											v-on:click="doAction(key)"
											:class="(currentAction === key) ? 'is-active': ''">
											{{ data.label }}
										</b-dropdown-item>
									</div>
									<div v-if="allowDeleteAction">
										<hr class="dropdown-divider" v-if="actions.length !== 0">

										<template v-if="trashedSelected">
											<b-dropdown-item v-on:click="restoreRows(selectedIds)">
												<span>Restore Selected</span>
											</b-dropdown-item>
											<b-dropdown-item v-on:click="deleteRows(false, false, true)">
												<span class="has-text-danger">Permanently Delete Selected</span>
											</b-dropdown-item>
										</template>
										<template v-else>
											<b-dropdown-item v-on:click="deleteRows">
												<span class="has-text-danger">Trash Selected</span>
											</b-dropdown-item>
										</template>
									</div>
								</b-dropdown>
							</div>
							<div class="datatable-selector-info" v-if="selectedIds.length">
								<small><em>
									<strong>{{ selectedIds.length }}</strong> out of
									<strong>{{ totalRows | numberFormat }}</strong> items selected
								</em></small>
							</div>
							<div class="datatable-refreshing" v-if="loadingBackground">
								<b-tooltip label="Data is refreshing in the background" position="is-right">
									<b-icon icon="sync-alt" size="small" class="fa-spin" />
								</b-tooltip>
							</div>
						</div>
					</div>
					<div class="column has-text-right">
						<div class="datatable-create-resource" v-if="userCan('create_' + this.model, this.model)">
							<template v-if="model === 'accounts' && userCan('create_simple_accounts', 'accounts')">
								<simple-account-button button-type="is-white" />
							</template>
							<template v-else>
								<b-button
									tag="router-link"
									icon-left="plus"
									class="has-icon"
									:type="createButtonType"
									:to="{path: transformCreateRouteLink($route)}">
									Create {{ model | modelSingular | capitalize }}
								</b-button>
							</template>
						</div>
						<div class="datatable-search" v-if="featureAvailable('search')">
							<b-field>
								<b-input v-model.trim="searchQuery"
								         :disabled="totalRows === 0 && searchQuery === ''"
								         placeholder="Search..."
								         type="search"
								         icon="search">
								</b-input>
								<button class="button is-text reset"
								        v-if="searchQuery"
								        v-on:click="clearSearch">
									<b-icon icon="times-circle"></b-icon>
								</button>
							</b-field>
						</div>
						<div class="datatable-filter" v-if="(!hasNoRows || hasActiveFilters) && showFilters && featureAvailable('filters')">
							<b-dropdown position="is-bottom-left">
								<button class="button is-bordered" slot="trigger"
								        :class="{'is-primary': hasActiveFilters}"
								        v-on:click="getFilters">
									<b-icon icon="filter" size="is-small"></b-icon>
									<b-icon icon="angle-down" size="is-small"></b-icon>
								</button>

								<div class="filters" :class="{'loading': loadingFilters}">
									<b-loading
										:is-full-page="false"
										:active.sync="loadingFilters"
										:can-cancel="false"
									/>

									<template v-if="filters.length !== 0" v-for="(data, filterKey) in filters">
										<div v-bind:key="`filter-${filterKey}`"
										     class="dropdown-item has-text-left datatable-filter-title">
											<p>
												<strong class="is-capitalized" v-text="data.label" />
												<span class="pull-right is-size-7 is-uppercase">filter</span>
											</p>
										</div>

										<b-field v-bind:key="`filter-select-${filterKey}`">
											<b-select :placeholder="`Select ${data.label} --`"
											          v-on:change.native="setFilter(filterKey, $event.target.value)"
											          :value="filtersArr[filterKey]"
											          :expanded="true">
												<option value="">
													View All
												</option>
												<option
													v-for="(label, index) in data.options"
													:key="`filter-${index}`"
													:value="index">
													{{ label }}
												</option>
											</b-select>
										</b-field>
									</template>
								</div>

								<hr class="dropdown-divider" v-if="filters.length !== 0">

								<b-dropdown-item v-on:click="resetFilter()">
									Reset All Filters
								</b-dropdown-item>
							</b-dropdown>
						</div>
						<div class="datatable-settings" v-if="featureAvailable('column_settings')">
							<b-button icon-left="cog" @click="showSettingsModal"></b-button>
						</div>
					</div>
				</div>
			</div>

			<transition name="animateHeight" mode="out-in" duration="1000">
				<div class="active-filter-notice" v-if="(!hasNoRows || hasActiveFilters) && showFilters && featureAvailable('filters') && (hasActiveFilters || searchQuery)"
				     v-on:click="resetFilter() + clearSearch()">
					<p>You're currently viewing filtered results, some data may not be visible.
						<span>Click here</span> to view all records.</p>
				</div>
			</transition>

			<div class="datatable-data-wrapper" v-on:mouseleave="hoverRow = null">
				<div :class="{'has-scrollbar': dtOverflowWidth > dtWidth}" class="datatable-top-scrollbar" ref="datatableTopScrollbar">
					<div :style="`width:` + dtOverflowWidth + `px`">&nbsp;</div>
				</div>
				<div class="datatable-data" ref="datatableData">
					<table class="table is-bordered is-fullwidth" v-if="hasNoRows">
						<tbody>
						<tr>
							<td class="p-t-15 p-b-15">
								<b-message type="is-warning">
									<strong>Oops!</strong> Looks like we can't find any data here, try adding a new record?
								</b-message>
							</td>
						</tr>
						</tbody>
					</table>
					<table class="table is-bordered is-striped is-hoverable is-fullwidth" v-if="!hasNoRows">
						<thead v-if="columns.length > 0 && !showEmptyRow">
						<tr>
							<th v-if="featureAvailable('actions')">
								<button v-on:click="currentSortCol = ''" v-if="currentSortCol !== ''" class="resetSort" title="Reset Sort Selection">
									<b-icon icon="redo" pack="fas" size="small" class="fa-flip-horizontal" />
								</button>
							</th>
							<th v-for="(header, index) in columns"
							    :key="`thead-${index}`"
							    :class="[{ 'is-sortable' : header.sortable }, header.classes]"
							    :style="(header.width) ? `width: ${header.width}` : ''"
							    v-if="header.disabled !== true"
							    v-on:click="sortColumn(header, currentSortDir)">
								{{ header.label }}
								<span v-if="header.sortable"
								      class="icon is-small"
								      :class="(currentSortCol === header.sort) ? 'is-visible' : 'is-invisible'">
                                    <b-icon
	                                    pack="fa"
	                                    icon="long-arrow-alt-up"
	                                    :class="(currentSortDir === 'asc') ? 'is-visible' : 'is-hidden'" />
                                    <b-icon
	                                    pack="fa"
	                                    icon="long-arrow-alt-down"
	                                    :class="(currentSortDir === 'desc') ? 'is-visible' : 'is-hidden'" />
                                </span>
							</th>
							<th class="datatable-resource-actions" v-if="featureAvailable('actions')"></th>
						</tr>
						</thead>
						<tbody v-if="columns.length > 0">
						<tr v-if="showEmptyRow">
							<td class="datatable-selector">
								<b-checkbox number />
							</td>
							<td v-for="(column, tdi) in columns" :key="`dttd-${tdi}`">
								--
							</td>
							<td>
								&nbsp;
							</td>
						</tr>
						<tr v-if="rows"
						    v-for="(row, rowIndex) in rows"
						    :key="`dt-${rowIndex}`"
						    :class="{'is-hovered': hoverRow === rowIndex, 'is-trashed': row.deleted_at}"
						    v-on:mouseover="hoverRow = rowIndex">
							<td class="datatable-selector" v-if="featureAvailable('actions')" v-bind:class="{ 'has-text-left': hasNewIndicator }">
								<b-checkbox
									:value="row.id"
									:native-value="row.id"
									v-model="selectedIds"
									number />
							</td>
							<td v-for="(column, tdi) in columns"
							    v-if="column.disabled !== true"
							    :class="[{'has-column-icon': hasRowIcons(row, column.src)}, column.classes]"
							    :key="`dttd-${tdi}`">
								<template v-if="!column.icon_only">
									<router-link class="router-link"
									             :to="{path: transformTableRouteLink(row, $route)}"
									             v-if="column.primary && linkPrimaryColumn && !row.deleted_at">
										<span>{{ getLabel(row, column.src) }}</span>
									</router-link>
									<span v-else>
                                        <column-icon :row="row" :src="column.src"></column-icon>
                                        {{ getLabel(row, column.src) }}
                                    </span>
								</template>
								<span :class="['column-icon', {'column-icon-only': column.icon_only}]"
								      v-for="(icon, index) in getRowIcons(row, column.src)"
								      :key="`ci-${index}`">
                                    <b-tooltip :label="icon.title">
                                        <b-icon :icon="icon.icon"
                                                :type="icon.type" />
                                    </b-tooltip>
                                </span>
							</td>
							<td class="datatable-resource-actions" v-if="featureAvailable('actions')">
								<button class="button is-text">
									<b-icon icon="ellipsis-v" size="is-small"></b-icon>
								</button>
							</td>
						</tr>
						</tbody>
					</table>
				</div>
				<aside class="datatable-fixed-menu" :class="[{'has-shadow': tableHasScrollbar}, {'has-scrollbar': dtOverflowWidth > dtWidth}]" v-if="!hasNoRows && featureAvailable('actions') && columns.length > 0">
					<table class="table is-bordered is-hoverable is-striped" v-if="rows">
						<thead>
						<tr>
							<th class="datatable-resource-actions">&nbsp;</th>
						</tr>
						</thead>
						<tbody>
						<tr v-for="(row, rowIndex) in rows"
						    :key="`dt-aside-${rowIndex}`"
						    :class="{'is-hovered': hoverRow === rowIndex, 'is-trashed': row.deleted_at}"
						    v-on:mouseover="hoverRow = rowIndex">
							<td class="datatable-resource-actions">
								<template v-if="model === 'account_invites'">
									<account-invite-row-actions :invite="row" v-on:invite-updated="getData"></account-invite-row-actions>
								</template>
								<template v-else>
									<b-dropdown :position="(rowIndex + 1) > ((totalRows < perPage ? totalRows : perPage) - 3) ? 'is-top-left' : 'is-bottom-left'">
										<button class="button is-text" slot="trigger">
											<b-icon icon="ellipsis-v" size="is-small"></b-icon>
										</button>
										<b-dropdown-item has-link v-if="!row.deleted_at">
											<router-link :to="{path: transformTableRouteLink(row, $route)}">
												View
											</router-link>
										</b-dropdown-item>
										<b-dropdown-item has-link v-if="!row.deleted_at">
											<router-link v-if="userCan('update_' + model, model)"
											             :to="{path: transformTableRouteLink(row, $route) + '/edit'}">
												Edit
											</router-link>
										</b-dropdown-item>
										<b-dropdown-item v-if="allowDeleteAction && !row.deleted_at"
										                 href="#" class="has-text-danger"
										                 v-on:click="deleteRows(rowIndex, row.id)">
											Trash
										</b-dropdown-item>
										<b-dropdown-item v-if="row.deleted_at" @click="restoreRows(row.id)">
											Restore
										</b-dropdown-item>
										<b-dropdown-item class="has-text-danger" v-if="row.deleted_at" @click="deleteRows(rowIndex, row.id, true)">
											Permanently Delete
										</b-dropdown-item>
									</b-dropdown>
								</template>
							</td>
						</tr>
						</tbody>
					</table>
				</aside>
			</div>
			<div class="datatable-footer">
				<nav class="datatable-navigation" role="navigation" aria-label="pagination">
					<div class="datatable-navigation-previous">
						<a class="button is-bordered"
						   v-on:click="toPage(1)"
						   :disabled="currentPage <= 1">
							First
						</a>
						<a class="button is-bordered"
						   v-on:click="previousPage"
						   :disabled="currentPage <= 1">
							Previous
						</a>
					</div>
					<div class="datatable-navigation-next">
						<a class="button is-bordered"
						   :disabled="lastPage <= currentPage"
						   v-on:click="nextPage">
							Next page
						</a>
						<a class="button is-bordered"
						   v-on:click="toPage(lastPage)"
						   :disabled="lastPage <= currentPage">
							Last
						</a>
					</div>
					<div class="datatable-totals" v-if="!loading" v-on:click="getData(false, false)" title="Click to refresh datatable">
						{{ currentRowsTo }} / {{ totalRows | numberFormat }} <span>items</span>
					</div>
				</nav>
			</div>
		</div>
		<modal modalType="settings"
		       v-if="settingsModalActive"
		       :data="model" small="true"
		       :performs-navigation="false"
		       v-on:modal-closed="closeSettingsModal"
		       v-on:settings-updated="handleSettingsUpdate"></modal>
	</div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import {
  DATATABLE_DELETE,
  DATATABLE_FILTERS,
  DATATABLE_ACTIONS,
  DATATABLE_ACTION,
  DATATABLE_RESTORE
} from '../../store/actions/datatable'
import debounce from 'lodash/debounce'
import getObjectValFromString from '@/functions/getObjectValFromString'
import getObj from '@/functions/getObj'
import hasObj from '@/functions/hasObj'
import stringifyObject from '@/functions/stringifyObject'
import AccountInviteRowActions from '@/components/AccountInviteRowActions'
import Modal from '@/components/Elements/Modal'
import ColumnIcon from '@/components/Elements/ColumnIcon'
import SimpleAccountButton from '../Account/SimpleAccountButton'
import { ACCOUNT_EXPORT_DOWNLOAD } from '../../store/actions/accounts'

export default {
  name: 'DataTable',
  props: {
    'model': {
      type: String
    },
    'modal': {
      type: String
    },
    'modalRoute': { // Allow custom router for the modal
      type: Object
    },
    'route': {
      type: String
    },
    'filter': {
      type: Object
    },
    'exportPrefix': {
      type: String,
      default: ''
    },
    queryParameters: {
      type: Object,
      default () {
        return {}
      }
    },
    transformTableRouteLink: {
      type: Function,
      default: (row, $route) => `${$route.path}/${row.id}`
    },
    transformCreateRouteLink: {
      type: Function,
      default: ($route) => `${$route.path}/create`
    }
  },
  components: {
    AccountInviteRowActions,
    Modal,
    ColumnIcon,
    SimpleAccountButton
  },
  data () {
    return {
      loadedOnce: false, // loaded the table data at least one time
      loading: 0, // is actively loading data, number of things still loading, 0 is done
      loadingFilters: true,
      loadingActions: true,
      loadingBackground: false,
      columns: [],
      filters: [],
      actions: [],
      filtersArr: {},
      currentAction: '',
      totalRows: 0, // total number of rows of data in the table
      perPage: 0, // number of rows per page
      currentPage: 1, // current page
      lastPage: 1, // last page number
      currentRowsTo: 0, // number of rows at the current page depth
      rows: {}, // contains the rows on the current page
      selectedAll: false, // tracks if select all checkbox is checked
      selectedIds: [], // ids of selected items from the rows
      settingsModalActive: false,
      scrollXPos: 0,
      dtWidth: 0,
      dtOverflowWidth: 0,
      hoverRow: null,
      hoverQueue: null
    }
  },
  watch: {
    currentPage: function () {
      if (this.loadedOnce === false) return
      this.setQuery('page', this.currentPage)
      this.getData()
    },
    currentSortDir: function () {
      if (this.loadedOnce === false) return
      this.getData()
    },
    currentSortCol: function () {
      if (this.loadedOnce === false) return
      this.getData()
    },
    perPage: function (newVal, oldVal) {
      if (this.loadedOnce === false || oldVal === 0) return
      if (newVal !== oldVal) {
        this.getData()
      }
    },
    currentFilters: {
      handler: function () {
        if (this.loadedOnce === false) return

        // Filter set from component
        // TODO current filter not set at load from component
        if (this.filter) {
          let filterKey = Object.keys(this.filter)[0]
          this.setFilter(filterKey, this.filter[filterKey])
          this.setQuery('filter__' + filterKey, '')
        }

        this.syncSelectedFilters()
        this.getData()
      },
      deep: true
    },
    modelUpdatedAt: function () {
      if (this.dataChanged === 'columns') {
        this.getColumns()
        this.$store.commit('datatableUpdated')
      }
      if (this.dataChanged === 'data' || (_.isObject(this.dataChanged) && this.dataChanged.type === 'data' && this.dataChanged.model === this.model)) {
        this.getUserSettings(false)
        this.getData(true)
        this.$store.dispatch('datatableIndex/resetIndex', this.model) // reset datatable data in the state
        this.$store.commit('datatableUpdated')
      }
    },
    selectedIds: function () {
      this.getActions()
    },
    loadedOnce: function () {
      this.setDtWidth()
      this.updateScrollXPos()
      if (this.$refs.datatableData !== undefined) {
        this.$refs.datatableData.addEventListener('scroll', this.updateScrollXPos)
        this.$refs.datatableTopScrollbar.addEventListener('scroll', this.updateTopScrollbarTop)
        window.addEventListener('resize', this.setDtWidth)
      }
    },
    navIsExpanded: function () {
      this.setDtWidth()
      this.updateScrollXPos()
    },
    hoverRow: function () {
      clearTimeout(this.hoverQueue)

      this.hoverQueue = setTimeout(() => {
        this.setDtWidth()
        this.updateScrollXPos()
      }, 250)
    }
  },
  computed: {
    ...mapState({
      navIsExpanded: 'navIsExpanded',
      systemLoading: state => state.loading,
      userSettings: state => state.userSettings,
      modelUpdatedAt: state => state.modelUpdatedAt,
      dataChanged: state => state.dataChanged,
      datatableState: state => state.datatableState,
      datatableIndex: state => state.datatableIndex.models
    }),
    ...mapGetters({
      isAuthenticated: 'isAuthenticated',
      pusher: 'pusher/init',
      userCan: 'userCan',
      datatableSearch: 'datatableSearch',
      datatableSortCol: 'datatableSortCol',
      datatableSortDir: 'datatableSortDir',
      datatableFilters: 'datatableFilters',
      datatableSubTitle: 'datatableSubTitle'
    }),
    uniqueModel () { // used for state tracking for this specific instance
      let key
      if (this.filter) key = stringifyObject(this.filter)
      if (key) return this.model + '_' + stringifyObject(this.filter)

      return this.model
    },
    searchQuery: {
      get: function () {
        return this.datatableSearch(this.uniqueModel)
      },
      set: debounce(function (newValue) {
        this.$store.commit('updateDatatableStateSearch', {
          'model': this.uniqueModel,
          'search': newValue
        })
      }, 800)
    },
    currentSortCol: { // currently selected row to sort
      get: function () {
        return this.datatableSortCol(this.uniqueModel)
      },
      set: function (newValue) {
        this.$store.commit('updateDatatableSortCol', {
          'model': this.uniqueModel,
          'sortCol': newValue
        })
      }
    },
    currentSortDir: { // direction of the current sort
      get: function () {
        return this.datatableSortDir(this.uniqueModel)
      },
      set: function (newValue) {
        this.$store.commit('updateDatatableSortDir', {
          'model': this.uniqueModel,
          'sortDir': newValue
        })
      }
    },
    currentFilters: { // query string variables for any search parameters
      get: function () {
        return this.datatableFilters(this.uniqueModel)
      },
      set: function (newValue) {
        this.$store.commit('updateDatatableStateFilters', {
          'model': this.uniqueModel,
          'filters': newValue
        })
      }
    },
    pusherChannel: function () {
      if (!this.pusher) return
      return this.pusher.subscribe('private-datatable.' + this.model)
    },
    isLoading: function () {
      return (this.loading > 0)
    },
    showEmptyRow: function () {
      if (this.loading > 0 && this.loadedOnce === false) return true
      if (this.rows.length === 0) return true
      if (this.loadedOnce === false) return true

      return false
    },
    hasNoRows: function () {
      // loaded at least one time but there are no rows
      if (this.loading > 0) return false

      if (this.filter) {
        return this.searchQuery === '' && Object.keys(this.rows).length === 0
      }

      return this.searchQuery === '' && Object.keys(this.rows).length === 0 && Object.keys(this.currentFilters).length === 0
    },
    createButtonType: function () {
      if (this.hasNoRows && !this.hasActiveFilters && !this.searchQuery) return 'is-primary'
      return 'is-white'
    },
    hasActiveFilters: function () {
      // only set filter button as primary color if active filters don't come from the parent component
      let cf = _.clone(this.currentFilters)

      _.forEach(this.filter, function (val, key) {
        key = 'filter__' + key
        delete cf[key]
      })

      return !!Object.keys(cf).length
    },
    showFilters: function () {
      return this.model !== 'account_invites'
    },
    allowDeleteAction: function () {
      if (this.model === 'account_invites') return false
      return this.userCan('delete_' + this.model, this.model)
    },
    linkPrimaryColumn: function () {
      return this.model !== 'account_invites'
    },
    tableHasScrollbar: function () {
      if (this.dtOverflowWidth - this.dtWidth === this.scrollXPos) return false // max scrolled
      if (this.dtOverflowWidth !== this.dtWidth) return true
      if (this.scrollXPos > 0) return true
      return false
    },
    hasNewIndicator: function () {
      return _.findIndex(this.rows, { is_new: true }) !== -1
    },
    trashedSelected: function () {
      let allTrashed = true
      let vm = this

      if (!vm.selectedIds.length) {
        return false
      }

      _.each(vm.selectedIds, function (selectedId) {
        let index = _.findIndex(vm.rows, { id: selectedId })
        if (index !== -1) {
          if (!vm.rows[index].deleted_at) {
            allTrashed = false
          }
        } else {
          allTrashed = false
        }
      })

      return allTrashed
    }
  },
  methods: {
    getObjectValFromString: getObjectValFromString,
    getObj: getObj,
    hasObj: hasObj,
    featureAvailable (feature) {
      const f = 'view_' + this.model + '_datatable_' + feature
      return this.userCan(f, this.model)
    },
    getUserSettings (withData = true) {
      const model = this.model
      if (this.userSettings.datatable !== undefined && this.userSettings.datatable[model] !== undefined) {
        let datatableSettings = this.userSettings['datatable'][model]
        this.perPage = datatableSettings.perPage

        // Changing the order
        let newColumns = []
        for (let columnKey in datatableSettings.columns) {
          for (let j = 0; j < this.columns.length; j++) {
            if (this.columns[j].src === columnKey) {
              newColumns.push(this.columns[j])
            }
          }
        }

        this.columns = newColumns

        // Toggling the columns
        for (let i = 0; i < Object.keys(datatableSettings.columns).length; i++) {
          for (let j = 0; j < this.columns.length; j++) {
            if (this.columns[i] === undefined) continue
            this.columns[i]['disabled'] = datatableSettings.columns[this.columns[i].src] === false
          }
        }

        // this is the first time table data is loaded
        if (withData === true) {
          this.getData()
        }
      } else {
        this.loading++
        this.$store.dispatch('getDatatableSettings', model).then(() => {
          // found settings, added to state
          if (this.userSettings.datatable !== undefined) {
            // double check to prevent infinite loop from user navigating too quickly
            this.getUserSettings(withData)
          }
          this.loading--
        }).catch(() => {
          this.$store.dispatch('getDefaultDatatableSettings', model).then(() => {
            // no settings found, default generated into state
            this.loading--
            if (this.userSettings.datatable !== undefined) {
              // double check to prevent infinite loop from user navigating too quickly
              this.getUserSettings(withData)
            }
          }).catch(() => {
            this.loading--
            // Read default columns and toggle since we have no user settings
            // and it failed to get defaults
            for (let i = 0; i < this.columns.length; i++) {
              if (this.columns[i].default !== true) {
                this.columns[i].disabled = true
              }
            }

            this.getData()
          })
        })
      }
    },
    setQuery (key, val) {
      if (val === '') {
        let query = Object.assign({}, this.$route.query)
        if (query[key] !== undefined) {
          delete query[key]
          this.$router.replace({ query })
        }
      } else {
        this.$router.push({
          query: Object.assign({}, this.$route.query, { [key]: val })
        })
      }
    },
    getQuery () {
      let qs = this.$route.query
      if (qs['search']) this.searchQuery = qs['search']
      if (qs['page']) this.currentPage = qs['page']

      // Filters from Query String
      for (let q in qs) {
        if (!qs.hasOwnProperty(q)) continue
        if (q.startsWith('filter__')) {
          if (Object.keys(this.filters).length === 0) {
            this.getFilters() // will run getQuery again
          } else {
            let filterKey = q.replace('filter__', '')
            if (this.filters[filterKey] !== undefined) {
              this.setFilter(filterKey, qs[q])
            }
          }
        }
      }

      // Filter set from component
      // Reset state data because this datatable is pre-filtered
      if (this.filter) {
        let filterKey = Object.keys(this.filter)[0]
        this.setFilter(filterKey, this.filter[filterKey])
        this.setQuery('filter__' + filterKey, '')
      }
    },
    clearSearch () {
      this.searchQuery = ''
    },
    getColumns () {
      let refresh = false
      const model = this.model
      this.columns = []
      if (this.datatableIndex[this.model] !== undefined && this.datatableIndex[this.model].columns !== undefined) {
        this.columns = this.datatableIndex[this.model].columns.data
        this.getUserSettings(true)
      } else {
        this.loading++
        this.$store.dispatch('datatableIndex/getColumns', { model }).then(() => {
          this.loading--
          this.getColumns(model)
        })
      }

      if (refresh) {
        this.$store.dispatch('datatableIndex/getColumns', { model })
      }
    },
    getData (force = false, noRefresh = false) {
      let refresh = false
      let loadFromState = false
      let response = {}

      let query = {
        page: this.currentPage,
        paginate: this.perPage,
        search: this.searchQuery,
        order: this.currentSortDir,
        sort: this.currentSortCol
      }
      query = _.merge(query, this.currentFilters, this.queryParameters)

      const model = this.model
      const stateQuery = stringifyObject(query)

      let filterPath = this.model + '.index.' + stateQuery
      if (hasObj(this.datatableIndex, filterPath)) {
        loadFromState = true
        response = getObj(this.datatableIndex, filterPath)
      }

      // data exists in the state, and we are not forcing a reload
      if (loadFromState && force === false) {
        this.loadedOnce = true
        this.handleGetDataResponse(response)
        refresh = true
        loadFromState = false
      } else {
        // No store data exists yet
        this.loading++
        this.$store.dispatch('datatableIndex/getIndex', { model, query }).then(response => {
          this.loading--
          this.handleGetDataResponse(response)
          this.getData(false, true) // first time loading the data, don't load it twice
        }).catch(err => {
          this.loading--
          this.handleGetDataResponse(err.response)
        })
      }

      if (refresh && noRefresh === false) {
        // reload store data in the background
        this.loadingBackground = true
        this.$store.dispatch('datatableIndex/getIndex', { model, query }).then(response => {
          this.handleGetDataResponse(response)
        }).catch(err => {
          this.handleGetDataResponse(err.response)
        })
      }
    },
    handleGetDataResponse (response) {
      this.loading = 0
      this.loadingBackground = false
      if (response.data.response !== undefined && response.data.response === 'error') {
        return
      }

      if (parseInt(this.currentPage) !== parseInt(response.meta.current_page)) return

      this.rows = response.data
      this.totalRows = response.meta.total
      this.perPage = this.perPage || response.meta.per_page
      this.lastPage = response.meta.last_page
      this.currentRowsTo = response.meta.to ? response.meta.to : 0

      if (this.lastPage < this.currentPage) {
        this.currentPage = 1
      }
    },
    getActions () {
      if (Object.keys(this.actions).length > 0) {
        return
      }
      const model = this.model
      this.$store.dispatch(DATATABLE_ACTIONS, { model }).then(response => {
        this.loadingActions = false
        if (response.data) {
          this.actions = response.data
        }
      })
    },
    dispatchAction: function (key, data) {
      this.$store.dispatch(DATATABLE_ACTION, data)
        .then(response => {
          if (this.actions[key].handle !== 'download') {
            this.getData()

            if (response) {
              if (response.message !== undefined) {
                this.$awn.success(response.message)
              }

              if (response.export) {
                this.$store.dispatch(ACCOUNT_EXPORT_DOWNLOAD, { model: response.export })
              }
            }
          }
        })
    },
    doAction: function (key) {
      let actionData = {
        model: this.model,
        action: key,
        handle: this.actions[key].handle,
        fileName: this.actions[key].supportsPrefix ? this.exportPrefix + this.actions[key].fileName : this.actions[key].fileName,
        selectedIds: this.selectedIds,
        input: {}
      }

      if (this.actions[key].prompt !== undefined) {
        let promptData = this.actions[key].prompt

        this.$buefy.dialog.prompt({
          message: promptData.message,
          inputAttrs: {
            type: promptData.type,
            placeholder: promptData.placeholder
          },
          onConfirm: (value) => {
            actionData.input[promptData.name] = value
            this.dispatchAction(key, actionData)
          }
        })
      } else {
        this.dispatchAction(key, actionData)
      }
    },
    syncSelectedFilters () {
      this.filtersArr = {}

      for (let f in this.currentFilters) {
        if (!this.currentFilters.hasOwnProperty(f)) continue
        let fKey = f

        if (f.startsWith('filter__')) {
          fKey = f.replace('filter__', '')
        }

        this.filtersArr[fKey] = this.currentFilters[f]
      }
    },
    getFilters () {
      if (Object.keys(this.filters).length > 0) {
        return
      }
      const model = this.model
      this.$store.dispatch(DATATABLE_FILTERS, { model }).then(response => {
        this.loadingFilters = false
        if (response.data) {
          this.filters = response.data
          this.getQuery()
          this.syncSelectedFilters()
        }
      })
    },
    setFilter: function (key, val) {
      if (key === undefined && val === undefined) return

      if (val === undefined || val === '') {
        this.setQuery('filter__' + key, '')
        this.$delete(this.currentFilters, 'filter__' + key)
        return
      }

      this.setQuery('filter__' + key, val)
      this.$set(this.currentFilters, 'filter__' + key, val)
      this.filtersArr[key] = val
    },
    resetFilter: function () {
      this.currentFilters = {}
      this.filters = []

      // Remove filters from Query String
      let qs = this.$route.query
      for (let q in qs) {
        if (!qs.hasOwnProperty(q)) continue
        if (q.startsWith('filter__')) {
          let filterKey = q.replace('filter__', '')
          this.setQuery('filter__' + filterKey, '')
        }
      }

      // Reset selected values
      this.filtersArr = []

      // Filter set from component
      if (this.filter) {
        let filterKey = Object.keys(this.filter)[0]
        this.setFilter(filterKey, this.filter[filterKey])
        this.setQuery('filter__' + filterKey, '')
      }

      this.getFilters()

      this.$store.commit('updateDatatableSubTitle', {
        'subtitle': '',
        'model': this.uniqueModel
      })
    },
    selectDefaultAction: function () {
      if (this.selectedAll === false) {
        this.selectNone()
      } else {
        this.selectEveryRecord()
      }
    },
    selectAll: function (input) {
      let selectedIds = this.selectedIds // scope
      if (input !== false) {
        this.rows.forEach(function (row, index) {
          if (!selectedIds.includes(row.id)) {
            selectedIds = selectedIds.concat(row.id)
          }
        })
        this.selectedIds = selectedIds
      } else {
        this.selectedIds = []
      }
    },
    selectEveryRecord: function () {
      this.selectAll()

      let selectedIds = this.selectedIds

      if (this.totalRows <= this.perPage) {
        this.rows.forEach(function (row, index) {
          if (!selectedIds.includes(row.id)) {
            selectedIds = selectedIds.concat(row.id)
          }
        })

        this.selectedIds = selectedIds
        this.selectedAll = true

        return
      }

      this.loading++

      let query = {
        page: 1,
        paginate: this.totalRows,
        search: this.searchQuery,
        order: this.currentSortDir
      }

      query = _.merge(query, this.currentFilters, this.queryParameters)
      const model = this.model

      this.$store.dispatch('datatableIndex/getIndex', { model, query }).then(response => {
        response.data.forEach(function (row, index) {
          if (!selectedIds.includes(row.id)) {
            selectedIds = selectedIds.concat(row.id)
          }
        })

        this.selectedIds = selectedIds
        this.selectedAll = true
        this.loading--
      }).catch(err => {
        this.handleGetDataResponse(err.response)
      })
    },
    selectNone: function () {
      this.selectedIds = []
      this.selectedAll = false
    },
    sortColumn (header, dir) {
      if (header.sortable) {
        this.currentSortDir = dir

        if (header.sort.toLowerCase() === this.currentSortCol.toLowerCase()) {
          this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc'
        } else {
          this.currentSortDir = 'desc'
        }

        this.currentSortCol = header.sort
      }
    },
    toPage (page) {
      this.currentPage = page
      this.selectedAll = false
    },
    nextPage () {
      if (this.lastPage > this.currentPage) {
        this.currentPage++
        this.selectedAll = false
      }
    },
    previousPage () {
      if (this.currentPage > 1) {
        this.currentPage--
        this.selectedAll = false
      }
    },
    hasRowIcons: function (row, src) {
      let arr = src.split('.')
      let target = arr.length - 2
      if (target < 0) target = 0

      if (row[arr[target]] === undefined) return false
      if (!row[arr[target]]) return false

      if (row[arr[target]].icons !== undefined) {
        return row[arr[target]].icons.length !== 0
      }

      return false
    },
    getRowIcons: function (row, src) {
      // TODO: It's labeled "Icons" but only supports a single icon, CSS needs adjustments if we ever use more then one
      let arr = src.split('.')
      let target = arr.length - 2
      if (target < 0) target = 0

      if (row[arr[target]] === undefined) return
      if (!row[arr[target]]) return

      if (row[arr[target]].icons !== undefined) {
        return row[arr[target]].icons
      }
    },
    getLabel: function (row, src) {
      let label

      if (src) {
        label = getObjectValFromString(row, src)
      }

      if (label instanceof Object) {
        return Object.keys(label).map(function (k) {
          return label[k]
        }).join(', ')
      }

      if (label) return label

      return '--'
    },
    deleteRows (index, Id, force = false) {
      let selectedIds

      if (!index && !Id) {
        selectedIds = this.selectedIds
      } else {
        selectedIds = [Id]
      }

      let action = 'delete'
      if (force) {
        action = 'permanently delete'
      }

      let deleteMessage = 'Are you sure you want to <b>' + action + '</b> this record? This action cannot be undone.'
      if (selectedIds.length > 1) {
        deleteMessage = 'Are you sure you want to <b>' + action + '</b> the selected records? This action cannot be undone.'
      }

      this.$buefy.dialog.confirm({
        title: 'Confirm Deletion',
        message: deleteMessage,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.loading++
          const model = this.model
          this.$store.dispatch(DATATABLE_DELETE, { model, selectedIds, force }).then(response => {
            this.selectedAll = false
            this.selectedIds = this.selectedIds.filter((el) => !selectedIds.includes(el))
            this.loading--
            this.$awn.success(response.message)
            this.getData()
            this.$store.commit('modelUpdated')
          }).catch(err => {
            this.loading = 0
            let errorMessage = err.response
            if (err.response) {
              errorMessage = err.response.data.message
            }
            this.$awn.alert(errorMessage)
          })
        }
      })
    },
    restoreRows (selectedIds) {
      if (!_.isArray(selectedIds)) {
        selectedIds = [selectedIds]
      }
      let message = 'Are you sure you want to <strong>restore</strong> '
      if (selectedIds.length > 1) {
        message += 'these records?'
      } else {
        message += 'this record?'
      }
      this.$buefy.dialog.confirm({
        title: 'Confirm Restoration',
        message,
        confirmText: 'Restore',
        type: 'is-primary',
        hasIcon: true,
        onConfirm: () => {
          this.loading++
          const model = this.model
          this.$store.dispatch(DATATABLE_RESTORE, { model, selectedIds }).then(response => {
            this.loading--
            this.$awn.success(response.message)
            this.getData()
            this.$store.commit('modelUpdated')
          }).catch(err => {
            this.loading = 0
            let errorMessage = err.response
            if (err.response) {
              errorMessage = err.response.data.message
            }
            this.$awn.alert(errorMessage)
          })
        }
      })
    },
    showSettingsModal () {
      this.settingsModalActive = true
    },
    closeSettingsModal () {
      this.settingsModalActive = false
    },
    handleSettingsUpdate () {
      this.getUserSettings(false)
      this.setDtWidth()
      this.updateScrollXPos()
      this.$store.commit('datatableUpdated')
    },
    updateTopScrollbarTop: function () {
      if (this.$refs.datatableData === undefined) return
      if (this.$refs.datatableTopScrollbar === undefined) return
      if (this.scrollXPos === this.$refs.datatableTopScrollbar.scrollLeft) return
      this.scrollXPos = this.$refs.datatableTopScrollbar.scrollLeft
      this.dtOverflowWidth = this.$refs.datatableData.scrollWidth
      this.$refs.datatableData.scrollLeft = this.scrollXPos
    },
    updateScrollXPos: function () {
      if (this.$refs.datatableData === undefined) return
      if (this.$refs.datatableTopScrollbar === undefined) return
      if (this.scrollXPos === this.$refs.datatableData.scrollLeft) return
      this.scrollXPos = this.$refs.datatableData.scrollLeft
      this.dtOverflowWidth = this.$refs.datatableData.scrollWidth
      this.$refs.datatableTopScrollbar.scrollLeft = this.scrollXPos
    },
    setDtWidth: debounce(function () {
      if (this.$refs.datatableStart !== undefined) this.dtWidth = this.$refs.datatableStart.clientWidth
      if (this.$refs.datatableData !== undefined) this.dtOverflowWidth = this.$refs.datatableData.scrollWidth
    }, 250)
  },
  created () {
    this.$store.commit('initDatatableState', this.uniqueModel)

    this.getQuery() // check for existing search/filter parameters
    this.getColumns() // get datatable columns
  },
  mounted () {
    if (this.pusherChannel) {
      this.pusherChannel.bind('model-updated', (data) => {
        let local = new Date(this.modelUpdatedAt)
        let pusher = new Date(data.updatedAt)

        if (pusher.getTime() > local.getTime()) {
          this.getData(true)
        }
      })
    }
  },
  beforeDestroy () {
    if (this.pusher) {
      this.pusher.unsubscribe('private-datatable.' + this.model)
      this.pusherChannel.unbind('model-updated')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Elements/Datatable.vue